/**
 * Invite codes page — manage invite-only registration codes.
 * Filter by status, create new codes, paginated table.
 */

import { useCallback, useEffect, useState } from "react";
import {
  type AdminInviteCode,
  type InviteCodeStatus,
  createInviteCode,
  listInviteCodes,
} from "../../api/admin-invites";
import { PageHeader } from "../../components/PageHeader";
import { StatsBar } from "../../components/StatsBar";
import { t } from "../../i18n";
import { listStyles as s } from "../../styles/list-page";

const PAGE_SIZE = 20;
const STATUS_OPTIONS = ["all", "available", "claimed", "expired"] as const;

export function InviteCodesPage(): React.JSX.Element {
  const [status, setStatus] = useState<string>("all");
  const [items, setItems] = useState<AdminInviteCode[]>([]);
  const [total, setTotal] = useState(0);
  const [cursors, setCursors] = useState<string[]>([]);
  const [page, setPage] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  const loadData = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const res = await listInviteCodes({
        status: status === "all" ? undefined : (status as InviteCodeStatus),
        cursor: page > 0 ? cursors[page - 1] : undefined,
        limit: PAGE_SIZE,
      });
      setItems(res.items);
      setTotal(res.total);
      if (res.next_cursor) {
        setCursors((prev) => {
          const next = [...prev];
          next[page] = res.next_cursor as string;
          return next;
        });
      }
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t("common.failed_to_load"));
    } finally {
      setLoading(false);
    }
  }, [status, page, cursors]);

  useEffect(() => {
    void loadData();
  }, [loadData]);

  const handleStatusChange = useCallback((v: string): void => {
    setStatus(v);
    setPage(0);
    setCursors([]);
  }, []);

  const handleCreate = useCallback(async (): Promise<void> => {
    setCreating(true);
    setError(null);
    try {
      await createInviteCode();
      setPage(0);
      setCursors([]);
      void loadData();
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : t("common.action_failed"));
    } finally {
      setCreating(false);
    }
  }, [loadData]);

  return (
    <div>
      <PageHeader
        title={t("invite_codes.title")}
        description={t("invite_codes.description")}
        actions={
          <button
            type="button"
            onClick={handleCreate}
            disabled={creating}
            style={pageStyles.createBtn}
          >
            {creating ? t("common.loading") : t("invite_codes.create_code")}
          </button>
        }
      />
      <StatsBar items={[{ label: t("stats.total"), value: total }]} />
      <FilterBar status={status} onStatusChange={handleStatusChange} />
      {error && <p style={s.error}>{error}</p>}
      {loading ? (
        <p style={s.loading}>{t("common.loading")}</p>
      ) : (
        <>
          <CodeTable items={items} />
          <CursorPagination
            page={page}
            total={total}
            hasNext={items.length === PAGE_SIZE}
            onChange={setPage}
          />
        </>
      )}
    </div>
  );
}

// ─── FilterBar ───

function FilterBar(props: {
  status: string;
  onStatusChange: (v: string) => void;
}): React.JSX.Element {
  return (
    <div style={s.filterBar}>
      <select
        value={props.status}
        onChange={(e) => props.onStatusChange(e.target.value)}
        style={s.select}
        aria-label="Filter by status"
      >
        {STATUS_OPTIONS.map((v) => (
          <option key={v} value={v}>
            {v === "all"
              ? t("invite_codes.all_status")
              : t(`invite_codes.status_${v}`)}
          </option>
        ))}
      </select>
    </div>
  );
}

// ─── Table ───

function CodeTable(props: {
  items: AdminInviteCode[];
}): React.JSX.Element {
  if (props.items.length === 0) {
    return <p style={s.empty}>{t("invite_codes.no_codes")}</p>;
  }
  return (
    <div style={s.tableWrap}>
      <table style={s.table}>
        <thead>
          <tr>
            <th style={s.th}>{t("invite_codes.col_code")}</th>
            <th style={s.th}>{t("invite_codes.col_issued_by")}</th>
            <th style={s.th}>{t("invite_codes.col_claimed_by")}</th>
            <th style={s.th}>{t("invite_codes.col_status")}</th>
            <th style={s.th}>{t("invite_codes.col_created")}</th>
            <th style={s.th}>{t("invite_codes.col_expires")}</th>
          </tr>
        </thead>
        <tbody>
          {props.items.map((c) => (
            <CodeRow key={c.id} code={c} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function CodeRow(props: { code: AdminInviteCode }): React.JSX.Element {
  const { code: c } = props;
  return (
    <tr>
      <td style={s.tdMono}>{c.code}</td>
      <td style={s.td}>{c.issued_by_name ?? c.issued_by ?? "-"}</td>
      <td style={s.td}>{c.claimed_by_name ?? c.claimed_by ?? "-"}</td>
      <td style={s.td}>
        <CodeStatusBadge status={c.status} />
      </td>
      <td style={s.td}>{formatDate(c.created_at)}</td>
      <td style={s.td}>
        {c.expires_at ? formatDate(c.expires_at) : t("invite_codes.never")}
      </td>
    </tr>
  );
}

// ─── StatusBadge ───

function CodeStatusBadge(props: {
  status: InviteCodeStatus;
}): React.JSX.Element {
  const color = STATUS_COLOR[props.status];
  return (
    <span style={{ ...s.badge, background: color.bg, color: color.fg }}>
      {t(`invite_codes.status_${props.status}`)}
    </span>
  );
}

const STATUS_COLOR: Record<InviteCodeStatus, { bg: string; fg: string }> = {
  available: { bg: "#dcfce7", fg: "#166534" },
  claimed: { bg: "#dbeafe", fg: "#1e40af" },
  expired: { bg: "#f3f4f6", fg: "#6b7280" },
};

// ─── CursorPagination ───

function CursorPagination(props: {
  page: number;
  total: number;
  hasNext: boolean;
  onChange: (v: number) => void;
}): React.JSX.Element {
  const totalPages = Math.max(1, Math.ceil(props.total / PAGE_SIZE));
  return (
    <div style={s.pagination}>
      <span style={s.pageInfo}>
        {t("invite_codes.page_info", {
          total: props.total,
          page: props.page + 1,
          totalPages,
        })}
      </span>
      <div style={s.pageButtons}>
        <button
          type="button"
          disabled={props.page === 0}
          onClick={() => props.onChange(props.page - 1)}
          style={s.pageBtn}
        >
          {t("common.previous")}
        </button>
        <button
          type="button"
          disabled={!props.hasNext}
          onClick={() => props.onChange(props.page + 1)}
          style={s.pageBtn}
        >
          {t("common.next")}
        </button>
      </div>
    </div>
  );
}

// ─── Helpers ───

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

// ─── Styles ───

const pageStyles: Record<string, React.CSSProperties> = {
  createBtn: {
    padding: "8px 16px",
    fontSize: 13,
    fontWeight: 500,
    borderRadius: 6,
    border: "none",
    background: "var(--color-primary)",
    color: "#fff",
    cursor: "pointer",
  },
};
